import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import { useBlogPosts } from "@/hooks/useBlogPosts";

const footerLinks = [
  { label: "About", href: "/about" },
  { label: "Blog", href: "/blog" },
  { label: "Best Guides", href: "/best" },
  { label: "Pricing", href: "/pricing" },
  { label: "FAQ", href: "/faq" },
  { label: "Contact", href: "/contact" },
  { label: "Privacy Policy", href: "/privacy-policy" },
  { label: "Cookie Policy", href: "/cookie-policy" },
  { label: "Terms", href: "/terms" },
  { label: "Refund Policy", href: "/refund-policy" },
];

const socialLinks = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/FinanciallyFitOnline",
    icon: Facebook,
    active: true,
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/financiallyfitonline",
    icon: Instagram,
    active: true,
  },
  {
    label: "LinkedIn",
    href: "#",
    icon: Linkedin,
    active: false,
  },
  {
    label: "X / Twitter",
    href: "#",
    icon: Twitter,
    active: false,
  },
];

export function PublicFooter() {
  const year = new Date().getFullYear();
  const { data: recentPosts = [] } = useBlogPosts();

  const latestPosts = recentPosts.slice(0, 9);

  return (
    <footer role="contentinfo" className="border-t border-border/50 bg-background mt-auto">
      <div className="container mx-auto px-4 py-10">
        {/* Top row */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
          {/* Brand */}
          <Link
            to="/"
            className="flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
            aria-label="Financially Fit home"
          >
            <img
              src="/pwa-192x192.png"
              alt="Financially Fit logo"
              width={40}
              height={40}
              className="w-10 h-10 rounded-xl"
              loading="lazy"
            />
            <div className="text-left">
              <p className="font-bold text-foreground">Financially Fit</p>
              <p className="text-xs text-muted-foreground">Personal Expense &amp; Budgeting Software</p>
            </div>
          </Link>

          {/* Nav links */}
          <nav aria-label="Footer navigation">
            <ul className="flex flex-wrap gap-x-6 gap-y-2" role="list">
              {footerLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    aria-label={link.label}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        {/* Recent Posts — helps crawlers discover all blog content */}
        {latestPosts.length > 0 && (
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-foreground mb-3">Recent Articles</h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5" role="list">
              {latestPosts.map((post) => (
                <li key={post.id}>
                  <Link
                    to={`/blog/${post.slug}`}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors line-clamp-1"
                  >
                    {post.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Social links */}
        <div className="flex items-center gap-3 mb-8">
          <span className="text-xs text-muted-foreground font-medium">Follow us:</span>
          <div className="flex items-center gap-2">
            {socialLinks.map((social) => {
              const Icon = social.icon;
              if (!social.active) return null;
              return (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Follow us on ${social.label}`}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-muted hover:bg-primary hover:text-primary-foreground text-muted-foreground transition-colors"
                >
                  <Icon className="h-4 w-4" />
                </a>
              );
            })}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mb-6 text-xs text-muted-foreground border border-border/40 rounded-lg px-4 py-3 bg-muted/30">
          <strong className="text-foreground">Software Disclaimer:</strong> Financially Fit is a personal expense &amp; budgeting software tool for data organisation and tracking purposes only.
          We do not provide financial, legal, investment, or tax advice. All data is managed solely by the user.
        </div>

        {/* Bottom row */}
        <div className="border-t border-border/50 pt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-xs text-muted-foreground text-center sm:text-left">
            © {year}{" "}
            <strong className="text-foreground">Financially Fit</strong> by{" "}
            <a
              href="https://knowledgecollegebd.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
              aria-label="Knowledge College BD website"
            >
              Knowledge College BD
            </a>
            . All rights reserved.
          </p>
          <address className="not-italic text-xs text-muted-foreground">
            Author:{" "}
            <span itemProp="author" className="text-foreground font-medium">
              Rakibul Hossen
            </span>
          </address>
        </div>
      </div>
    </footer>
  );
}
